import { Controller } from "@hotwired/stimulus"

// Duree d'affichage du feedback d'erreur (ms)
const ERROR_FEEDBACK_MS = 2000

// Liste des quiz d'une session live/presentiel, cote ETUDIANT : reception
// des quiz lances, selection/soumission d'une reponse, feedback correct/
// incorrect, et badge des quiz en attente de reponse (l'etat « non repondus »
// vit ici — le badge en est une simple projection). Issu de la decomposition
// de live_session_quiz_controller (787 l., Lot 7.6).
export default class extends Controller {
  static targets = ["quizList", "quizCard", "emptyState", "quizBadge"]
  static values = {
    quizzesUrl: String,
    labelClosed: { type: String, default: "Clôturé" },
    labelConfirmAnswer: { type: String, default: "Confirmer votre réponse ?" },
    labelCorrect: { type: String, default: "Correct !" },
    labelIncorrect: { type: String, default: "Incorrect" },
    labelSubmit: { type: String, default: "Valider" }
  }

  connect() {
    this.unansweredQuizIds = new Set()
    this.selectedIndices = new Map()
    this.initializeUnansweredQuizzes()
  }

  // Initialiser les quiz actifs non repondus a partir du DOM
  initializeUnansweredQuizzes() {
    this.quizCardTargets.forEach(card => {
      const quizId = parseInt(card.dataset.quizId)
      const hasOptionButtons = card.querySelector('button[data-action*="toggleOption"]')
      const statusBadge = card.querySelector("[data-status-badge]")
      const isClosed = statusBadge && statusBadge.textContent.trim() === this.labelClosedValue

      if (hasOptionButtons && !isClosed) {
        this.unansweredQuizIds.add(quizId)
      }
    })

    this.updateQuizBadge()
  }

  // --- Evenements dispatches par live-session / classroom-session controller ---

  onQuizLaunched(event) {
    const { quiz_id, question, options, allow_multiple } = event.detail
    const safeQuizId = parseInt(quiz_id)

    if (this.hasQuizListTarget) {
      const card = this.buildStudentQuizCardElement(safeQuizId, question, options, allow_multiple)
      this.quizListTarget.prepend(card)
    }
    if (this.hasEmptyStateTarget) this.emptyStateTarget.remove()

    this.unansweredQuizIds.add(safeQuizId)
    this.updateQuizBadge()
  }

  onQuizClosed(event) {
    const { quiz_id } = event.detail
    const safeQuizId = parseInt(quiz_id)
    this.markQuizClosed(safeQuizId)

    this.unansweredQuizIds.delete(safeQuizId)
    this.updateQuizBadge()
  }

  onQuizDeleted(event) {
    const { quiz_id } = event.detail
    const safeQuizId = parseInt(quiz_id)
    const card = this.element.querySelector(
      `[data-quiz-id="${safeQuizId}"][data-student-quiz-board-target="quizCard"]`
    )
    if (card) card.remove()

    this.unansweredQuizIds.delete(safeQuizId)
    this.updateQuizBadge()
  }

  // --- Selection/deselection d'une option ---

  toggleOption(event) {
    event.preventDefault()

    const button = event.currentTarget
    const quizId = parseInt(button.dataset.quizId)
    const optionIndex = parseInt(button.dataset.optionIndex)
    const quizCard = button.closest("[data-allow-multiple]")
    const allowMultiple = quizCard?.dataset.allowMultiple === "true"

    if (!this.selectedIndices.has(quizId)) {
      this.selectedIndices.set(quizId, new Set())
    }

    const selected = this.selectedIndices.get(quizId)

    if (selected.has(optionIndex)) {
      selected.delete(optionIndex)
      button.classList.remove("ring-2", "ring-blue-500", "bg-slate-600")
      button.classList.add("bg-slate-700")
    } else {
      // Si reponse unique, deselectionner les autres
      if (!allowMultiple) {
        const optionsArea = button.closest("[data-options-area]")
        optionsArea.querySelectorAll('button[data-action*="toggleOption"]').forEach(btn => {
          btn.classList.remove("ring-2", "ring-blue-500", "bg-slate-600")
          btn.classList.add("bg-slate-700")
        })
        selected.clear()
      }
      selected.add(optionIndex)
      button.classList.add("ring-2", "ring-blue-500", "bg-slate-600")
      button.classList.remove("bg-slate-700")
    }

    // Activer/desactiver le bouton de validation
    const submitBtn = this.element.querySelector(
      `button[data-submit-quiz][data-quiz-id="${quizId}"]`
    )
    if (submitBtn) {
      submitBtn.disabled = selected.size === 0
    }
  }

  // --- Soumission de la reponse (avec confirmation) ---

  async submitAnswer(event) {
    event.preventDefault()

    const button = event.currentTarget
    const quizId = parseInt(button.dataset.quizId)

    if (!this.selectedIndices.has(quizId) || this.selectedIndices.get(quizId).size === 0) return

    // Confirmation obligatoire avant envoi
    if (!window.confirm(this.labelConfirmAnswerValue)) return

    const indices = Array.from(this.selectedIndices.get(quizId))
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    button.disabled = true

    try {
      const response = await fetch(
        `${this.quizzesUrlValue}/${quizId}/answer`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": token,
            "Accept": "application/json"
          },
          body: JSON.stringify({ selected_indices: indices })
        }
      )

      if (response.ok) {
        const data = await response.json()
        this.showAnswerFeedback(quizId, indices, data.is_correct, data.correct_indices)
        this.unansweredQuizIds.delete(quizId)
        this.updateQuizBadge()
      } else {
        button.disabled = false
        this.flashError(button)
      }
    } catch (error) {
      console.error("[Quiz] Answer error:", error)
      button.disabled = false
      this.flashError(button)
    }
  }

  // --- Badge des quiz en attente ---

  updateQuizBadge() {
    if (!this.hasQuizBadgeTarget) return

    const count = this.unansweredQuizIds.size
    if (count > 0) {
      this.quizBadgeTarget.textContent = count > 99 ? "99+" : count
      this.quizBadgeTarget.classList.remove("hidden")
      this.quizBadgeTarget.classList.add("inline-flex")
    } else {
      this.quizBadgeTarget.classList.add("hidden")
      this.quizBadgeTarget.classList.remove("inline-flex")
    }
  }

  // --- Feedback apres reponse ---

  showAnswerFeedback(quizId, selectedIndices, isCorrect, correctIndices) {
    const quizCard = this.element.querySelector(`#session_quiz_${quizId}`)
    if (!quizCard) return

    const optionsArea = quizCard.querySelector("[data-options-area]")
    if (!optionsArea) return

    // Remplacer les boutons par des indicateurs de resultat
    const buttons = Array.from(optionsArea.querySelectorAll('button[data-action*="toggleOption"]'))
    buttons.forEach(btn => {
      const index = parseInt(btn.dataset.optionIndex)
      const optionText = btn.textContent.trim()
      const isSelected = selectedIndices.includes(index)
      const isOptionCorrect = correctIndices.includes(index)

      const resultDiv = document.createElement("div")
      resultDiv.classList.add("rounded-lg", "px-3", "py-2", "flex", "items-center", "gap-2")

      if (isSelected && isOptionCorrect) {
        resultDiv.classList.add("bg-emerald-900/50", "border", "border-emerald-500")
        this.appendIcon(resultDiv, "✓", "text-emerald-400")
        this.appendText(resultDiv, optionText, "text-emerald-300")
      } else if (isSelected && !isOptionCorrect) {
        resultDiv.classList.add("bg-red-900/50", "border", "border-red-500")
        this.appendIcon(resultDiv, "✗", "text-red-400")
        this.appendText(resultDiv, optionText, "text-red-300")
      } else if (!isSelected && isOptionCorrect) {
        resultDiv.classList.add("bg-emerald-900/30", "border", "border-emerald-500/50")
        this.appendIcon(resultDiv, "✓", "text-emerald-400/70")
        this.appendText(resultDiv, optionText, "text-emerald-300/70")
      } else {
        resultDiv.classList.add("bg-slate-700/50")
        this.appendText(resultDiv, optionText, "text-slate-500")
      }

      btn.replaceWith(resultDiv)
    })

    // Retirer le bouton Valider
    const submitBtn = quizCard.querySelector("button[data-submit-quiz]")
    if (submitBtn) submitBtn.remove()

    // Afficher le bandeau correct/incorrect
    const banner = document.createElement("div")
    banner.classList.add("mt-3", "text-center", "text-sm", "font-bold")
    banner.classList.add(isCorrect ? "text-emerald-400" : "text-red-400")
    banner.textContent = isCorrect ? this.labelCorrectValue : this.labelIncorrectValue
    optionsArea.parentElement.appendChild(banner)
  }

  // --- Marquer un quiz comme cloture ---

  markQuizClosed(quizId) {
    const badge = this.element.querySelector(
      `[data-status-badge][data-quiz-id="${quizId}"]`
    )
    if (badge) {
      badge.className = "text-xs text-slate-500 flex-shrink-0"
      badge.textContent = this.labelClosedValue
    }

    // Desactiver les boutons d'option et le bouton valider
    const quizCard = this.element.querySelector(`#session_quiz_${quizId}`)
    if (quizCard) {
      quizCard.querySelectorAll('button[data-action*="toggleOption"]').forEach(btn => {
        btn.disabled = true
        btn.classList.add("opacity-50", "cursor-not-allowed")
        btn.classList.remove("hover:bg-slate-600")
      })

      const submitBtn = quizCard.querySelector("button[data-submit-quiz]")
      if (submitBtn) {
        submitBtn.disabled = true
        submitBtn.classList.add("opacity-50", "cursor-not-allowed")
      }
    }
  }

  // --- Construction DOM securisee de la carte etudiant ---

  buildStudentQuizCardElement(quizId, question, options, allowMultiple) {
    const card = document.createElement("div")
    card.id = `session_quiz_${quizId}`
    card.className = "bg-slate-800 rounded-lg p-3"
    card.dataset.studentQuizBoardTarget = "quizCard"
    card.dataset.quizId = quizId
    card.dataset.allowMultiple = allowMultiple

    // En-tete
    const header = document.createElement("div")
    header.className = "flex items-start justify-between gap-2 mb-3"

    const title = document.createElement("p")
    title.className = "text-sm font-medium text-white"
    title.textContent = question

    const dot = document.createElement("span")
    dot.className = "w-2 h-2 rounded-full bg-emerald-500 animate-pulse flex-shrink-0 mt-1.5"
    dot.dataset.statusBadge = ""
    dot.dataset.quizId = quizId

    header.appendChild(title)
    header.appendChild(dot)
    card.appendChild(header)

    // Options
    const optionsArea = document.createElement("div")
    optionsArea.className = "space-y-2"
    optionsArea.dataset.optionsArea = ""
    optionsArea.dataset.quizId = quizId

    options.forEach((option, index) => {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = "w-full text-left bg-slate-700 hover:bg-slate-600 rounded-lg px-3 py-2 text-sm text-white transition-colors duration-200"
      btn.dataset.action = "click->student-quiz-board#toggleOption"
      btn.dataset.quizId = quizId
      btn.dataset.optionIndex = index
      btn.textContent = option
      optionsArea.appendChild(btn)
    })

    card.appendChild(optionsArea)

    // Bouton Valider
    const submitBtn = document.createElement("button")
    submitBtn.type = "button"
    submitBtn.className = "w-full mt-3 student-btn-primary rounded-lg px-3 py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
    submitBtn.dataset.action = "click->student-quiz-board#submitAnswer"
    submitBtn.dataset.submitQuiz = ""
    submitBtn.dataset.quizId = quizId
    submitBtn.disabled = true
    submitBtn.textContent = this.labelSubmitValue

    card.appendChild(submitBtn)

    return card
  }

  // --- Helpers DOM ---

  appendIcon(parent, symbol, colorClass) {
    const span = document.createElement("span")
    span.className = `font-bold ${colorClass}`
    span.textContent = symbol
    parent.appendChild(span)
  }

  appendText(parent, text, colorClass) {
    const span = document.createElement("span")
    span.className = `text-sm ${colorClass}`
    span.textContent = text
    parent.appendChild(span)
  }

  flashError(element) {
    element.classList.add("ring-2", "ring-red-500")
    setTimeout(() => {
      element.classList.remove("ring-2", "ring-red-500")
    }, ERROR_FEEDBACK_MS)
  }
}
